import { useEffect, useState, useContext, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { BlynIcon } from './BlynIcon'
import { store } from './legacy/state/store'
import { useNewProject } from './legacy/modals/newProject'
import { openActionConfirm } from './legacy/modals/actionConfirm'
import type { BlynProject } from '@/blyn/services/projectsService'
import { listConversations, deleteConversation, type BlynConversation } from '@/blyn/services/conversationsService'
import { displayConversationLabel } from '@/blyn/services/messageAttachments'
import { listProjects, archiveProjectById } from '@/blyn/services/projectsService'
import { toastApiError } from '@/blyn/services/apiToast'
import { ToastContext } from '@/lib/toast'
import {
  Rail, RailHeader, RailContent, RailSection,
  SearchBar, SectionHeader, SectionCount,
  ProjectRow, ProjectIcon, ProjectBadge,
  ConvRow, EmptyText,
} from './ProjectsRail.styles'

export default function ProjectsRail() {
  const navigate = useNavigate()
  const appToast = useContext(ToastContext)
  const openNewProject = useNewProject()
  const [currentProjectId, setCurrentProjectId] = useState<string>(() => store.get('currentProjectId') as string ?? '')
  const [currentConversationId, setCurrentConversationId] = useState<string>(() => store.get('currentConversationId') as string ?? '')
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('blyn:pinnedProjects') ?? '[]')) } catch { return new Set() }
  })
  const [allProjects, setAllProjects] = useState<BlynProject[]>([])

  const pinnedProjects = allProjects.filter(p => pinnedIds.has(p.id))
  const activeProjects = allProjects.filter(p => !pinnedIds.has(p.id))
  const [recentConvs, setRecentConvs] = useState<BlynConversation[]>([])

  // Sequence numbers guard against out-of-order responses: an older fetch
  // (e.g. one already in flight when a project is created) can resolve
  // after a newer one and must not clobber it with stale data.
  const projectsFetchSeq = useRef(0)
  const convsFetchSeq = useRef(0)

  useEffect(() => {
    function refetchProjects() {
      const seq = ++projectsFetchSeq.current
      listProjects().then(projects => { if (seq === projectsFetchSeq.current) setAllProjects(projects) })
    }
    function refetchConversations() {
      const seq = ++convsFetchSeq.current
      listConversations()
        .then(convs => { if (seq === convsFetchSeq.current) setRecentConvs(convs) })
        .catch(err => toastApiError("Failed to load conversations", err))
    }

    // store.select fires its callback immediately with the current value,
    // so this alone covers the initial load — no separate mount-time fetch needed.
    const u1 = store.select('currentProjectId', (v: string) => setCurrentProjectId(v))
    const u2 = store.select('currentConversationId', (v: string) => setCurrentConversationId(v))
    const u3 = store.select('conversationsVersion', refetchConversations)
    const u4 = store.select('projectsVersion', refetchProjects)
    // The tenant isn't known yet on the very first render (the host resolves
    // auth asynchronously), so the initial fetch above can go out scoped to
    // the fallback tenant and come back empty. Refetch once the real
    // tenant-bearing currentUser lands in the store (layout.tsx sets it).
    const u5 = store.select('currentUser', () => { refetchProjects(); refetchConversations() })
    return () => { u1?.(); u2?.(); u3?.(); u4?.(); u5?.() }
  }, [])

  function handlePinProject(e: React.MouseEvent, id: string) {
    e.stopPropagation()
    setPinnedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      localStorage.setItem('blyn:pinnedProjects', JSON.stringify([...next]))
      return next
    })
  }

  function handleProjectClick(id: string) {
    store.set({ currentProjectId: id, mobileRailOpen: false })
    navigate(`/blyn/project-workspace?id=${id}`)
  }

  function openConversation(conv: BlynConversation) {
    store.set({
      currentConversationId: conv.id,
      currentProjectId: conv.projectId ?? store.get('currentProjectId'),
      mobileRailOpen: false,
    })
    navigate(`/blyn/new-conversation?id=${conv.id}`)
  }

  async function handleDeleteConversation(e: React.MouseEvent, id: string) {
    e.stopPropagation()
    const conv = recentConvs.find(c => c.id === id)
    openActionConfirm({
      title: "Delete conversation",
      sub: "Are you sure you want to delete this conversation?",
      confirmLabel: "Delete",
      warning: "This action cannot be undone.",
      onConfirm: async () => {
        const success = await deleteConversation(id)
        if (success) {
          appToast.success(conv?.title ? `"${conv.title}" deleted` : "Conversation deleted", "Deleted")
          setRecentConvs(prev => prev.filter(c => c.id !== id))
          if (currentConversationId === id) {
            store.set({ currentConversationId: '' })
            navigate('/blyn/new-conversation')
          }
        }
      }
    })
  }

  async function handleDeleteProject(e: React.MouseEvent, id: string) {
    e.stopPropagation()
    const project = allProjects.find(p => p.id === id)
    openActionConfirm({
      title: "Delete project",
      sub: "Are you sure you want to delete this project?",
      confirmLabel: "Delete",
      warning: "This action cannot be undone.",
      onConfirm: async () => {
        const success = await archiveProjectById(id)
        if (success) {
          appToast.success(project?.name ? `"${project.name}" deleted` : "Project deleted", "Deleted")
          setAllProjects(prev => prev.filter(p => p.id !== id))
          setPinnedIds(prev => { const next = new Set(prev); next.delete(id); localStorage.setItem('blyn:pinnedProjects', JSON.stringify([...next])); return next })
          if (currentProjectId === id) {
            store.set({ currentProjectId: '' })
            navigate('/blyn/new-conversation')
          }
        }
      }
    })
  }

  return (
    <Rail className="projects-rail" component="aside" aria-label="Projects">
      {/* Header */}
      <RailHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '10px' }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.01em', fontFamily: 'var(--font-display)' }}>
            Projects
          </Typography>
          <Tooltip title="New Project" placement="right">
            <IconButton
              size="small"
              onClick={() => { store.set({ mobileRailOpen: false }); openNewProject() }}
              sx={{
                width: 28, height: 28, borderRadius: 'var(--r-sm)',
                color: 'var(--text-primary)',
                '&:hover': { bgcolor: 'var(--bg-hover)', color: 'var(--text-primary)' },
              }}
            >
              <BlynIcon name="plus" size={14} />
            </IconButton>
          </Tooltip>
        </Box>

        <SearchBar
          role="button"
          tabIndex={0}
          onClick={() => store.set({ paletteOpen: true })}
          onKeyDown={(e) => e.key === 'Enter' && store.set({ paletteOpen: true })}
        >
          <Box sx={{ color: 'var(--text-primary)', display: 'flex', flexShrink: 0 }}>
            <BlynIcon name="search" size={12} />
          </Box>
          <Box
            component="input"
            type="text"
            placeholder="Search projects, KB, history…"
            readOnly
            tabIndex={-1}
            sx={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              fontSize: 12, color: 'var(--text-primary)', minWidth: 0,
              pointerEvents: 'none',
              '&:focus': { outline: 'none' },
              '&:focus-visible': { outline: 'none' },
              '&::placeholder': { color: 'var(--text-tertiary)' },
            }}
          />
          <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-primary)', bgcolor: 'var(--bg-app)', px: '5px', py: '2px', borderRadius: '4px', border: '1px solid var(--line)' }}>
            ⌘K
          </Typography>
        </SearchBar>
      </RailHeader>

      {/* Content */}
      <RailContent>
        {/* Pinned */}
        <RailSection>
          <SectionHeader>
            <span>PINNED</span>
            <SectionCount>{pinnedProjects.length}</SectionCount>
          </SectionHeader>
          {pinnedProjects.length === 0 ? (
            <EmptyText>No Pinned Projects</EmptyText>
          ) : pinnedProjects.map(p => (
            <ProjectItem key={p.id} project={p} active={p.id === currentProjectId} pinned onClick={handleProjectClick} onPin={handlePinProject} onDelete={handleDeleteProject} />
          ))}
        </RailSection>

        {/* Active */}
        <RailSection>
          <SectionHeader>
            <span>ACTIVE</span>
            <SectionCount>{activeProjects.length}</SectionCount>
          </SectionHeader>
          {activeProjects.length === 0 ? (
            <EmptyText>No Active Projects Yet</EmptyText>
          ) : activeProjects.map(p => (
            <ProjectItem key={p.id} project={p} active={p.id === currentProjectId} pinned={false} onClick={handleProjectClick} onPin={handlePinProject} onDelete={handleDeleteProject} />
          ))}
        </RailSection>

        {/* Recent conversations */}
        <RailSection>
          <SectionHeader>
            <span>RECENT CONVERSATIONS</span>
            <Tooltip title="New Conversation" placement="right">
              <IconButton
                size="small"
                onClick={() => { store.set({ mobileRailOpen: false }); navigate('/blyn/new-conversation') }}
                sx={{
                  width: 26, height: 26, borderRadius: 'var(--r-sm)',
                  color: 'var(--text-primary)',
                  '&:hover': { bgcolor: 'var(--bg-hover)', color: 'var(--accent-text)' },
                }}
              >
                <BlynIcon name="plus" size={14} />
              </IconButton>
            </Tooltip>
          </SectionHeader>
          {recentConvs.length === 0 ? (
            <EmptyText>
              No Recent Conversations
              <Typography component="span" sx={{ display: 'block', fontSize: 10.5, color: 'var(--text-tertiary)', mt: '3px' }}>
                Start one to build your first project
              </Typography>
            </EmptyText>
          ) : recentConvs.map(c => (
            <ConvRow
              key={c.id}
              data-active={c.id === currentConversationId ? 'true' : 'false'}
              role="button"
              tabIndex={0}
              onClick={() => openConversation(c)}
              onKeyDown={(e) => e.key === 'Enter' && openConversation(c)}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {displayConversationLabel(c.title)}
                </Typography>
                <Typography sx={{ fontSize: 10, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', mt: '1px' }}>
                  {c.updatedAt}
                </Typography>
              </Box>
              <Tooltip title="Delete conversation" placement="right">
                <IconButton
                  className="delete-btn"
                  size="small"
                  onClick={(e) => handleDeleteConversation(e, c.id)}
                  sx={{
                    width: 24, height: 24, borderRadius: 'var(--r-sm)',
                    color: 'var(--text-secondary)',
                    '&:hover': { bgcolor: 'var(--bg-app)', color: 'var(--error-text)' },
                  }}
                >
                  <BlynIcon name="delete" size={12} />
                </IconButton>
              </Tooltip>
            </ConvRow>
          ))}
        </RailSection>
      </RailContent>
    </Rail>
  )
}

function ProjectItem({ project, active, pinned, onClick, onPin, onDelete }: {
  project: BlynProject
  active: boolean
  pinned: boolean
  onClick: (id: string) => void
  onPin?: (e: React.MouseEvent, id: string) => void
  onDelete?: (e: React.MouseEvent, id: string) => void
}) {
  return (
    <Tooltip title={`${project.name} · ${project.statusLabel}`} placement="bottom">
      <ProjectRow
        data-active={active ? 'true' : 'false'}
        role="button"
        tabIndex={0}
        onClick={() => onClick(project.id)}
        onKeyDown={(e) => e.key === 'Enter' && onClick(project.id)}
      >
        <ProjectIcon style={{ background: project.avatarBg, color: project.avatarFg }}>
          {project.avatar}
        </ProjectIcon>

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {project.name}
          </Typography>
          <Box sx={{ fontSize: 10.5, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', display: 'flex', alignItems: 'center', gap: '4px', mt: '1px' }}>
            {project.audience !== '·' ? `${project.audience} audience` : project.statusLabel?.toLowerCase()}
            <Box component="span" sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: 'currentColor', flexShrink: 0 }} />
            {project.drops !== '·' ? `${project.drops} drops` : '·'}
          </Box>
        </Box>

        {project.status === 'live' ? (
          <ProjectBadge style={{ background: 'var(--success-bg)', color: 'var(--success-text)' }}>LIVE</ProjectBadge>
        ) : (
          <ProjectBadge>{project.statusLabel?.slice(0, 5)}</ProjectBadge>
        )}

        {onPin && (
          <Tooltip title={pinned ? 'Unpin project' : 'Pin project'} placement="right">
            <IconButton
              className="pin-btn"
              data-pinned={pinned ? 'true' : 'false'}
              size="small"
              onClick={(e) => onPin(e, project.id)}
              sx={{
                width: 24, height: 24, borderRadius: 'var(--r-sm)',
                color: pinned ? 'var(--accent-text)' : 'var(--text-secondary)',
                '&:hover': { bgcolor: 'var(--bg-app)', color: 'var(--accent-text)' },
              }}
            >
              <BlynIcon name="pin" size={12} />
            </IconButton>
          </Tooltip>
        )}

        {onDelete && (
          <Tooltip title="Delete project" placement="right">
            <IconButton
              className="delete-btn"
              size="small"
              onClick={(e) => onDelete(e, project.id)}
              sx={{
                width: 24, height: 24, borderRadius: 'var(--r-sm)',
                color: 'var(--text-secondary)',
                '&:hover': { bgcolor: 'var(--bg-app)', color: 'var(--error-text)' },
              }}
            >
              <BlynIcon name="delete" size={12} />
            </IconButton>
          </Tooltip>
        )}
      </ProjectRow>
    </Tooltip>
  )
}
